/**
 * The heading contract between a shell and the page inside it (UX 1.0a).
 *
 * One `h1` per page (WCAG 1.3.1 / 2.4.6). The shell's menu label used to be
 * the only title, rendered as an `h2` — so a page had no `h1` at all, or two
 * competing titles once a page drew its own. The rule now:
 *
 *  - DEFAULT: no `PageHeader` on the current route → the shell renders its
 *    label as the `h1`. A page that never adopts `PageHeader` stays correct.
 *  - A mounted `PageHeader` registers itself (in `useLayoutEffect`, so the
 *    shell re-renders before paint and there is no flash of two `h1`s) and
 *    unregisters on unmount. While registered, the shell label is NOT a
 *    heading; if it says the same as the page title it is hidden, and on a
 *    phone it is hidden anyway (no room, and it repeats the page).
 *  - A route change resets the contract: a registration counts only for the
 *    route it was made on, so a header left over from the previous route can
 *    never demote the next page's shell label. A `PageHeader` that stays
 *    mounted across a route change (same page component, new params)
 *    re-registers for the new route.
 *  - The shell never uses `h2` — that level belongs to the page's sections.
 *
 * Shells consume `useShellHeading(label)`. Nothing here writes anywhere.
 */

import type { SxProps, Theme } from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

interface Registration {
  /** Route the registration was made on. */
  routeKey: string;
  title: string;
}

interface PageHeaderContextValue {
  routeKey: string;
  registrations: ReadonlyMap<number, Registration>;
  register: (id: number, registration: Registration) => void;
  unregister: (id: number) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

/**
 * Wrap a shell's content (header + main) in this. Must sit inside a Router:
 * the current pathname is the route key.
 */
export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [registrations, setRegistrations] = useState<ReadonlyMap<number, Registration>>(
    () => new Map(),
  );

  const register = useCallback((id: number, registration: Registration) => {
    setRegistrations((prev) => {
      const current = prev.get(id);
      if (current && current.routeKey === registration.routeKey && current.title === registration.title) {
        return prev;
      }
      const next = new Map(prev);
      next.set(id, registration);
      return next;
    });
  }, []);

  const unregister = useCallback((id: number) => {
    setRegistrations((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ routeKey: pathname, registrations, register, unregister }),
    [pathname, registrations, register, unregister],
  );

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

let nextId = 1;

/**
 * Used by `PageHeader`: registers for the current route while mounted.
 * Outside a provider (a page rendered on its own, a test) it is a no-op.
 */
export function useRegisterPageHeader(title: string): void {
  const ctx = useContext(PageHeaderContext);
  const idRef = useRef<number>(0);
  if (idRef.current === 0) idRef.current = nextId++;

  const register = ctx?.register;
  const unregister = ctx?.unregister;
  const routeKey = ctx?.routeKey;

  useLayoutEffect(() => {
    if (!register || !unregister || routeKey === undefined) return undefined;
    const id = idRef.current;
    register(id, { routeKey, title });
    return () => unregister(id);
  }, [register, unregister, routeKey, title]);
}

function normalise(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

export interface ShellHeading {
  /** A `PageHeader` is mounted for the current route. */
  pageHasHeader: boolean;
  /** Element for the shell label: `h1` by default, `div` while a PageHeader owns the h1. Never `h2`. */
  component: 'h1' | 'div';
  /** The page title says the same as the shell label. */
  duplicate: boolean;
  /**
   * Spread onto the shell's title `Typography` (keep the shell's own `sx` for
   * typography; merge `sx` here for visibility). `variant` stays the shell's
   * choice — `component` decides the element, so `variant="h2"` styling no
   * longer produces an `<h2>`.
   */
  titleProps: {
    component: 'h1' | 'div';
    'aria-hidden'?: true;
    sx: SxProps<Theme>;
  };
}

/**
 * For the shells. Default (no provider, no PageHeader) is the shell `h1`.
 * Hiding uses `visibility`, not `display`, so the label keeps its
 * `flexGrow` slot and the header actions do not jump left.
 * With a PageHeader: the label becomes a plain `div`; it is hidden on phones
 * (< 600 px), and hidden everywhere — including from assistive tech — when it
 * duplicates the page title. A non-duplicate label (menu section above a
 * detail page title) stays visible from 600 px up as context, not a heading.
 */
export function useShellHeading(label: string): ShellHeading {
  const ctx = useContext(PageHeaderContext);

  const active = ctx
    ? [...ctx.registrations.values()].filter((r) => r.routeKey === ctx.routeKey)
    : [];
  const pageHasHeader = active.length > 0;
  const duplicate = pageHasHeader && active.some((r) => normalise(r.title) === normalise(label));

  if (!pageHasHeader) {
    return {
      pageHasHeader,
      component: 'h1',
      duplicate: false,
      titleProps: { component: 'h1', sx: {} },
    };
  }
  if (duplicate) {
    return {
      pageHasHeader,
      component: 'div',
      duplicate,
      titleProps: { component: 'div', 'aria-hidden': true, sx: { visibility: 'hidden' } },
    };
  }
  return {
    pageHasHeader,
    component: 'div',
    duplicate,
    titleProps: { component: 'div', sx: { visibility: { xs: 'hidden', sm: 'visible' } } },
  };
}
